/**
 * TimePicker — 시/분 선택기
 *
 * DateTimePicker의 시간 선택 UI를 독립 컴포넌트로 분리.
 * 스크롤 리스트 + 직접 입력 방식.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import styles from './TimePicker.module.css'

export interface TimePickerProps {
  /** "HH:MM" 형식 문자열 */
  value?: string
  /** 시간 변경 콜백 ("HH:MM" 형식) */
  onChange?: (time: string) => void
  /** 필드 레이블 */
  label?: string
  /** 에러 메시지 */
  error?: string
  /** 플레이스홀더 */
  placeholder?: string
  /** 분 단위 간격 (기본 5) */
  minuteStep?: number
  /** 비활성 상태 */
  disabled?: boolean
  /** 추가 클래스명 */
  className?: string
}

function parseTime(value?: string): { h: number; m: number } | null {
  if (!value) return null
  const match = value.match(/^(\d{1,2}):(\d{2})$/)
  if (!match) return null
  const h = parseInt(match[1], 10)
  const m = parseInt(match[2], 10)
  if (h < 0 || h > 23 || m < 0 || m > 59) return null
  return { h, m }
}

function formatTime(h: number, m: number): string {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function formatDisplay(h: number, m: number): string {
  const period = h < 12 ? '오전' : '오후'
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${period} ${display}:${String(m).padStart(2, '0')}`
}

function buildHourOptions(): { value: number; label: string }[] {
  const opts: { value: number; label: string }[] = []
  for (let h = 0; h < 24; h++) {
    const period = h < 12 ? '오전' : '오후'
    const display = h === 0 ? 12 : h > 12 ? h - 12 : h
    opts.push({ value: h, label: `${period} ${display}시` })
  }
  return opts
}

function buildMinuteOptions(step: number): { value: number; label: string }[] {
  const opts: { value: number; label: string }[] = []
  for (let m = 0; m < 60; m += step) {
    opts.push({ value: m, label: `${String(m).padStart(2, '0')}분` })
  }
  return opts
}

export function TimePicker({
  value,
  onChange,
  label,
  error,
  placeholder = '시간을 선택하세요',
  minuteStep = 5,
  disabled = false,
  className,
}: TimePickerProps) {
  const [open, setOpen] = useState(false)
  const parsed = parseTime(value)

  const [hour, setHour] = useState(() => parsed?.h ?? 12)
  const [minute, setMinute] = useState(() => {
    if (!parsed) return 0
    return Math.round(parsed.m / minuteStep) * minuteStep
  })

  const [hourInputValue, setHourInputValue] = useState(() => String(hour).padStart(2, '0'))
  const [minuteInputValue, setMinuteInputValue] = useState(() => String(minute).padStart(2, '0'))

  const wrapperRef = useRef<HTMLDivElement>(null)
  const hourListRef = useRef<HTMLDivElement>(null)
  const minuteListRef = useRef<HTMLDivElement>(null)

  const hourOptions = useMemo(() => buildHourOptions(), [])
  const minuteOptions = useMemo(() => buildMinuteOptions(minuteStep), [minuteStep])

  // 외부 value 변경 추적
  useEffect(() => {
    if (parsed) {
      const h = parsed.h
      const m = Math.round(parsed.m / minuteStep) * minuteStep
      setHour(h)
      setMinute(m)
      setHourInputValue(String(h).padStart(2, '0'))
      setMinuteInputValue(String(m).padStart(2, '0'))
    }
  }, [value, minuteStep]) // eslint-disable-line react-hooks/exhaustive-deps

  const emitChange = useCallback((h: number, m: number) => {
    onChange?.(formatTime(h, m))
  }, [onChange])

  const handleHourSelect = useCallback((h: number) => {
    setHour(h)
    setHourInputValue(String(h).padStart(2, '0'))
    emitChange(h, minute)
  }, [minute, emitChange])

  const handleMinuteSelect = useCallback((m: number) => {
    setMinute(m)
    setMinuteInputValue(String(m).padStart(2, '0'))
    emitChange(hour, m)
  }, [hour, emitChange])

  const handleHourInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '')
    if (val.length <= 2) setHourInputValue(val)
  }, [])

  const handleHourInputBlur = useCallback(() => {
    let h = parseInt(hourInputValue, 10)
    if (isNaN(h)) h = hour
    h = Math.max(0, Math.min(23, h))
    setHour(h)
    setHourInputValue(String(h).padStart(2, '0'))
    emitChange(h, minute)
  }, [hourInputValue, hour, minute, emitChange])

  const handleMinuteInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '')
    if (val.length <= 2) setMinuteInputValue(val)
  }, [])

  const handleMinuteInputBlur = useCallback(() => {
    let m = parseInt(minuteInputValue, 10)
    if (isNaN(m)) m = minute
    m = Math.max(0, Math.min(59, m))
    m = Math.round(m / minuteStep) * minuteStep
    if (m >= 60) m = 60 - minuteStep
    setMinute(m)
    setMinuteInputValue(String(m).padStart(2, '0'))
    emitChange(hour, m)
  }, [minuteInputValue, minute, minuteStep, hour, emitChange])

  const handleToggle = useCallback(() => {
    if (!disabled) setOpen((v) => !v)
  }, [disabled])

  // 외부 클릭 시 닫기
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // ESC 키로 닫기
  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  // popover 열릴 때 선택된 항목으로 스크롤
  useEffect(() => {
    if (!open) return
    requestAnimationFrame(() => {
      hourListRef.current?.querySelector(`.${styles.timeItemSelected}`)
        ?.scrollIntoView({ block: 'center' })
      minuteListRef.current?.querySelector(`.${styles.timeItemSelected}`)
        ?.scrollIntoView({ block: 'center' })
    })
  }, [open])

  return (
    <div ref={wrapperRef} className={cn(styles.wrapper, className)}>
      {label && <label className={styles.label}>{label}</label>}

      <button
        type="button"
        className={cn(
          styles.trigger,
          disabled && styles.disabled,
          error && styles.triggerError,
        )}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className={cn(styles.triggerText, !parsed && styles.placeholder)}>
          {parsed ? formatDisplay(hour, minute) : placeholder}
        </span>
        <svg className={styles.clockIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className={styles.popover}>
          <div className={styles.timeSection}>
            <div className={styles.timeColumn}>
              <input
                type="text"
                inputMode="numeric"
                className={styles.timeInput}
                value={hourInputValue}
                onChange={handleHourInputChange}
                onBlur={handleHourInputBlur}
                maxLength={2}
              />
              <div className={styles.timeList} ref={hourListRef}>
                {hourOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={cn(styles.timeItem, hour === opt.value && styles.timeItemSelected)}
                    onClick={() => handleHourSelect(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.timeColumn}>
              <input
                type="text"
                inputMode="numeric"
                className={styles.timeInput}
                value={minuteInputValue}
                onChange={handleMinuteInputChange}
                onBlur={handleMinuteInputBlur}
                maxLength={2}
              />
              <div className={styles.timeList} ref={minuteListRef}>
                {minuteOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={cn(styles.timeItem, minute === opt.value && styles.timeItemSelected)}
                    onClick={() => handleMinuteSelect(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  )
}
